import IdenticalItemDialog from 'App/shared/components/IdenticalItemDialog/IdenticalItemDialog'
import { fetchDuplicateItems } from 'App/modules/items/contentItems'
import { axios } from 'shared/services/axios'
import CryptoJS from 'crypto-js'
import get from 'lodash/get'

const CSV_FILE_TYPE_UTF_8 = 'text/csv; charset=utf-8'

const CSV_FILE_TYPES = [
  'text/comma-separated-values',
  'text/csv',
  'application/csv',
  'application/excel',
  'application/vnd.ms-excel',
  'application/vnd.msexcel'
]

export function uploadImage (file, fileType = null) {
  fileType = fileType || getImageFileType(file)

  return getUploadUrl({ file, uploadType: fileType })
    .then((data) => putFile(data, file, fileType))
    .then(response => response.secure_url)
}

export async function uploadFile (file, options = { fileDestination: null, isSystemFile: false, fileProvider: null, detectDuplicates: false, organizationId: null }) {
  const existingItemData = options.detectDuplicates ? await checkExistingFile(file, options.organizationId) : null
  if (existingItemData) {
    return handleExistingFile(existingItemData, file, options)
  } else {
    return handleUploadFile(file, options.fileDestination, options.isSystemFile, options.fileProvider)
  }
}

function handleExistingFile(
  existingItemData,
  file,
  options = {
    fileDestination: null,
    isSystemFile: false,
    fileProvider: null,
    detectDuplicates: false,
    allowReplaceDuplicateWithExisting: false,
    organizationId: null
  }) {
  return new Promise((resolve, reject) => {
    IdenticalItemDialog({
      existingItemData,
      allowReplaceDuplicateWithExisting: options.allowReplaceDuplicateWithExisting,
      onUseExisting: () => resolve(existingItemData),
      onUseNew: () => resolve(handleUploadFile(file, options.fileDestination, options.isSystemFile, options.fileProvider)),
      onError: (e) => reject(e)
    })
  })
}

async function checkExistingFile(file, organizationId) {
  try {
    const size = file.size
    const duplicateBySize = await fetchDuplicateItems(organizationId, { size })
    if (!duplicateBySize) {
      return null
    }
    // Only if a file with the same size exists we will go and calculate the checksum
    const checksumData = await calculateChecksum(file)
    return await fetchDuplicateItems(organizationId, checksumData)
  } catch (error) {
    console.error('Error checking for existing file', error)
    return null // in case where calculateChecksum fails, we should not block the upload
  }
}

function handleUploadFile(file, fileDestination, isSystemFile, fileProvider) {
  const fileType = getFileType(file.type)
  return getUploadUrl({ file, fileDestination, isSystemFile, fileProvider })
    .then((data) => putFile(data, file, fileType))
}

async function calculateChecksum(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const binaryStr = event.target.result
        const md5 = CryptoJS.MD5(CryptoJS.enc.Latin1.parse(binaryStr))
        const sha1 = CryptoJS.SHA1(CryptoJS.enc.Latin1.parse(binaryStr))
        resolve({
          md5: md5.toString(CryptoJS.enc.Hex),
          sha1: sha1.toString(CryptoJS.enc.Hex)
        })
      } catch (error) {
        reject(error)
      }
    }
    reader.onerror = reject
    reader.readAsBinaryString(file)
  })
}

export function putFile (data, file, fileType) {
  const config = {
    method: data.method,
    url: data.put_url,
    noBearer: true,
    headers: {
      ...data.headers,
      'Content-type': fileType
    }
  }

  if (data.upload_in_chunks) {
    return sendFileInChunks(file, data, config)
  } else {
    config.data = createData(file, data.params)
    return axios(config)
      .then((response) => {
        return parseUploadResponse(response, data)
      })
  }
}

function sendFileInChunks (file, data, config) {
  let chunkStart = 0
  return processChunks()

  function processChunks () {
    const chunkEnd = Math.min(chunkStart + data.chunk_size, file.size)
    const chunk = file.slice(chunkStart, chunkEnd)

    config.headers['Content-Range'] = `bytes ${chunkStart}-${chunkEnd - 1}/${file.size}`
    config.data = createData(chunk, data.params)

    return axios(config).then((response) => {
      if (chunkEnd < file.size) {
        chunkStart += data.chunk_size
        return processChunks()
      } else {
        return parseUploadResponse(response, data)
      }
    })
  }
}

function parseUploadResponse (response, data) {
  const responseData = get(response.data, ['entries', 0], response.data)
  const fileId = responseData.public_id || responseData.id || data.file_url
  // sha1 for box, etag for Cloudinary.
  // sha1 should be first because box also returns etag which is not equal to sha1 and is not the checksum
  const checksum = responseData.sha1 || responseData.etag

  return {
    ...data,
    ...responseData,
    file_id: fileId,
    storage_provider_id: data.file_provider_type,
    file_meta: {
      file_size: responseData.bytes || responseData.size,
      checksum,
      duration: responseData.duration,
      height: responseData.height,
      width: responseData.width
    }
  }
}

export function getUploadUrl ({ file, uploadType, fileDestination = null, isSystemFile = false, fileProvider = null }) {
  const data = {
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
    is_public: true,
    type: uploadType,
    file_destination: fileDestination,
    is_system_file: isSystemFile,
    file_provider: fileProvider
  }

  return axios.post('/api/v1/upload_urls', data)
    .then(response => response.data)
}

function createData (file, params) {
  if (params) {
    const formData = new FormData()
    formData.append('file', file)
    Object.keys(params).forEach(key => formData.append(key, params[key]))
    file = formData
  }

  return file
}

function getImageFileType (file) {
  const fileType = file.type
  return fileType.substring(0, fileType.indexOf('/'))
}

function getFileType (fileType) {
  return CSV_FILE_TYPES.includes(fileType) ? CSV_FILE_TYPE_UTF_8 : fileType
}